import { useMediaQuery, useTheme, Box, Card, CardContent, Typography, List, ListItem, ListItemText, Button, IconButton, ListItemAvatar, Avatar, Divider, Alert, CardMedia, darken, SxProps, Theme, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import React, { useMemo } from 'react';
import { Currencies, CURRENCY_SYMBOLS, QuickOrderInfo, QuickOrderProduct, ResourceListItem } from '../../../types';
import { QuickOrderTotals } from '../../orders/components/OrderPaymentTotals';
import productPlaceholderImage from '../../../assets/product-placeholder.png';
import { Trans, useTranslation } from 'react-i18next';
import { translateUOM } from '../../../i18n/utils';
import { alpha } from '@mui/material/styles';
import ReactPixel from 'react-facebook-pixel';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { useShippingRegionContext } from '../../../contexts/ShippingRegionContext';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { Link as RouterLink } from 'react-router-dom';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import CheckIcon from '@mui/icons-material/Check';

const IS_ADD_TO_CART_TEMPORARILY_DISABLED = true;
const ENABLED_VENDOR_IDS: number[] = [530];

interface CreateQuickOrderCardProps {
	onCreate: () => void;
}

export const CreateQuickOrderCard = ({ onCreate }: CreateQuickOrderCardProps) => {
	const isSmUp = useMediaQuery(useTheme().breakpoints.up('sm'));

	return (
		<Card sx={{ 
			minWidth: isSmUp ? '350px' : '300px',
			maxWidth: '350px',
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
			alignItems: 'center',
			cursor: 'pointer',
			'&:hover': {
				bgcolor: 'action.hover',
			},
		}} onClick={onCreate}>
			<Box sx={{ 
				display: 'flex', 
				flexDirection: 'column',
				alignItems: 'center',
				gap: 2,
				p: 4,
			}}>
				<ShoppingBagIcon sx={{ fontSize: 48, color: 'primary.main' }} />
				<Typography variant="h6" color="primary">
					Create a build kit
				</Typography>
				<Typography variant="body2" color="text.secondary" align="center">
					Help DIYers begin with this project by offering the required items.
				</Typography>
			</Box>
		</Card>
	);
};

export const QuickOrderCardVendorInvitation = () => {
	const isSmUp = useMediaQuery(useTheme().breakpoints.up('sm'));
	const { t } = useTranslation();
	const theme = useTheme();
	return (
		<Card sx={{ 
			minWidth: isSmUp ? '350px' : '300px',
			maxWidth: '350px',
			flex: isSmUp ? '1 0 350px' : '1 0 300px',
			bgcolor: 'transparent',
		}}
		elevation={0}
		>
			<CardContent>
				<Typography variant="h6" color="text.secondary">
					{t('planViewQuickOrderCard.vendorInvitationTitle')}
				</Typography>
				<Typography variant="body2" color="text.secondary">
					<Trans i18nKey='planViewQuickOrderCard.vendorInvitation' components={{ joinAsVendorLink: <RouterLink to="https://hello.diyfeed.com/#vendors" style={{color: theme.palette.primary.main}} /> }} />
				</Typography>
			</CardContent>
		</Card>
	);
};


interface ProductListItemProps {
	item: QuickOrderProduct;
	selectedProductQuantities?: Map<number, string>;
	onItemClick?: (product: QuickOrderProduct) => void;
	onQuantityChange?: (productId: number, newQuantity: number) => void;
	disabled?: boolean;
	maxQuantity?: number;
	currency: Currencies;
}

const ProductListItem = ({ item, selectedProductQuantities, onItemClick, onQuantityChange, disabled, maxQuantity, currency }: ProductListItemProps) => {
	const quantity = selectedProductQuantities?.get(item.id);
	const theme = useTheme();
	const { t } = useTranslation();
	const currencySymbol = CURRENCY_SYMBOLS[currency];
	return (
		<ListItem 
			key={item.id} 
			// divider={i < list.products.length - 1}
			sx={{ flexDirection: 'column', alignItems: 'stretch', userSelect: 'none' }}
			disableGutters
		>
			<Box sx={{ 
				display: 'flex', 
				justifyContent: 'space-between', 
				alignItems: 'center',
				width: '100%',
				gap: 1,
			}}>
				<Box sx={{ display: 'flex', alignItems: 'center', flex: 1, cursor: onItemClick ? 'pointer' : 'default', filter: disabled ? 'opacity(0.5)' : 'none' }} onClick={() => onItemClick?.(item)}>
					<ListItemAvatar>
						<Avatar
							src={item.imgUrl ? item.imgUrl : productPlaceholderImage}
							alt={item.name}
							variant="rounded"
							sx={{ width: 72, height: 72, mr: 2, background: alpha(theme.palette.getContrastText(theme.palette.background.default), 0.12) }}
						/>
					</ListItemAvatar>
					<ListItemText 
						primary={item.name}
						secondary={t('planViewQuickOrderCard.pricePerUnit', { currency: currencySymbol, price: item.pricePerUnit, uom: translateUOM(item.uom) })}
						sx={{ wordBreak: 'break-word' }}
					/>
				</Box>
				<Box>
					{Number(quantity) > 0 ? (
						<Button variant="contained" size="small" disableRipple disableElevation sx={{cursor: 'default', '&:hover': {bgcolor: 'primary.main'}}}>
							{currencySymbol}{(Number(item.pricePerUnit) * Number(quantity)).toFixed(2)}
						</Button>
					) : <Button
						variant="text"
						onClick={() => onQuantityChange?.(item.id, 1)}
						disabled={disabled}
						sx={{
							px: 1.5,
							minWidth: 'auto',
							typography: 'body2',
							textTransform: 'uppercase',
						}}
					>
						{t('planViewQuickOrderCard.add')}
					</Button>}
				</Box>
			</Box>
			{item.digitalDelivery && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
				<CloudDownloadIcon sx={{ fontSize: 16, color: 'success.main' }} />
				<Typography variant='body2' color='text.secondary'>{t('productView.digitalDelivery', { count: Number(item.documentsCount) })}</Typography>
			</Box>}
			{disabled && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
				<LocalShippingIcon sx={{ fontSize: 16, color: 'error.main' }} />
				<Typography variant='body2' color={Number(quantity) > 0 ? 'error.main' : 'text.secondary'}>{t('planViewQuickOrderCard.notAvailableInRegion')}</Typography>
			</Box>}
			{Number(quantity) > 0 && <QuickOrderCardAdjustQuantitySection 
				quantity={Number(quantity)} 
				uom={item.uom} 
				onQuantityChange={newQuantity => onQuantityChange?.(item.id, newQuantity)} 
				isAddDisabled={Boolean(maxQuantity && Number(quantity) >= maxQuantity)}
			/>}
		</ListItem>
	);
};

interface QuickOrderCardProps {
	list: Pick<QuickOrderInfo, 'id' | 'title' | 'products' | 'additionalFees' | 'shippingRegions' | 'publishedAt' | 'currency' | 'vendorId'> & { vendor: { company: { profileId: string, coverImgUrl: string, name: string } } };
	resources: ResourceListItem[];
	onProductQuantityChange?: (QuickOrderProduct: QuickOrderProduct, newQuantity: number) => void;
	onAddToCart?: (products: QuickOrderProduct[]) => void;
	onEdit?: () => void;
	onItemClick?: (product: QuickOrderProduct) => void;
	isAuthor?: boolean;
	isDraft?: boolean;
	selectedProductQuantities: Map<number, string>;
	selectedQuickOrders: Set<number>;
}

export const QuickOrderCard = ({ list, resources, onProductQuantityChange, onAddToCart, onEdit, onItemClick, isAuthor, isDraft, selectedProductQuantities, selectedQuickOrders }: QuickOrderCardProps) => {
	const isSmUp = useMediaQuery(useTheme().breakpoints.up('sm'));
	const theme = useTheme();
	const { t } = useTranslation();
	const { selectedRegion } = useShippingRegionContext();
	const doesShippingRegionMatch = useMemo(() => {
		if (!selectedRegion) return false;
		return list.shippingRegions.includes(selectedRegion);
	}, [list.shippingRegions, selectedRegion]);

	// Create a lookup map for resources by their ID
	const resourcesById = new Map<number, ResourceListItem>(
		resources.map((resource: ResourceListItem) => [resource.id, resource])
	);

	/**
	 * separate products into categories based on their corresponding resources:
	 * - material products are linked to material recipe items
	 * - tool products are linked to tool recipe items
	 * - optional products are either not linked to any recipe item or linked to an optional item
	 */
	const materialProducts = useMemo(() => list.products.filter(product => {
		const resource = resourcesById.get(product.resourceId);
		return !resource || (resource && !resource?.isOptional && !resource?.isTool);
	}), [list.products, resourcesById]);
	const toolProducts = useMemo(() => list.products.filter(product => {
		const resource = resourcesById.get(product.resourceId);
		return resource && !resource?.isOptional && resource?.isTool;
	}), [list.products, resourcesById]);
	const optionalProducts = useMemo(() => list.products.filter(product => {
		const resource = resourcesById.get(product.resourceId);
		return resource && resource?.isOptional;
	}), [list.products, resourcesById]);
	// const missingResources = useMemo(() => resources.filter(resource => !resource.isOptional && !resource.isTool && !list.products.some(product => product.resourceId === resource.id)), [resources, list.products]);
	// const missingTools = useMemo(() => resources.filter(resource => resource.isTool && !list.products.some(product => product.resourceId === resource.id)), [resources, list.products]);

	const selectedProductsWithQuantities = useMemo(() => list.products.filter(product => selectedProductQuantities.get(product.id) && Number(selectedProductQuantities.get(product.id) as string) > 0).map(product => ({
		...product,
		quantity: selectedProductQuantities.get(product.id) as string
	})), [list.products, selectedProductQuantities]);
	const nonDigitalProductsSelectedByProductId = useMemo(() => selectedProductsWithQuantities.reduce((acc, product) => !product.digitalDelivery ? acc.add(product.id) : acc, new Set<number>()), [selectedProductsWithQuantities]);
	const areNonDigitalProductsSelected = useMemo(() => nonDigitalProductsSelectedByProductId.size > 0, [nonDigitalProductsSelectedByProductId]);
	const isVendorWhitelisted = useMemo(
		() => ENABLED_VENDOR_IDS.includes(list.vendorId),
		[list.vendorId]
	);
	const isAddToCartTemporarilyDisabledForVendor = useMemo(
		() => IS_ADD_TO_CART_TEMPORARILY_DISABLED && !isVendorWhitelisted,
		[isVendorWhitelisted]
	);
	const isCheckoutDisabled = useMemo(
		() =>
			selectedProductsWithQuantities.length === 0 ||
			(!doesShippingRegionMatch && areNonDigitalProductsSelected),
		[selectedProductsWithQuantities, doesShippingRegionMatch, areNonDigitalProductsSelected]
	);

	const handleAddToCart = () => {
		onAddToCart?.(selectedProductsWithQuantities);
		ReactPixel.track('AddToCart');
	};

	return (
		<Card sx={{ 
			minWidth: isSmUp ? '350px' : '300px',
			maxWidth: '350px',
			flex: isSmUp ? '1 0 350px' : '1 0 300px',
		}}>
			<CardMedia
				component="img"
				height="100"	
				image={list.vendor.company.coverImgUrl}
				alt={list.vendor.company.name}
				sx={{
					objectFit: 'cover',
				}}
			/>
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: alpha(theme.palette.text.primary, 0.05) }}>
				<Typography variant="subtitle2" sx={{ py: 1, px: 2 }}>
					{/* TODO: Solve the links logic properly */}
					<Trans i18nKey='planViewQuickOrderCard.cartBy' components={{ strong: <strong />, vendorProfileLink: <RouterLink to={`/shop/${list.vendor.company.profileId}`} style={{color: theme.palette.primary.main}} /> }} values={{ vendorName: list.vendor.company.name }} />
				</Typography>
				{isAuthor && onEdit && (
					<IconButton 
						size="small" 
						onClick={onEdit}
					>
						<EditIcon />
					</IconButton>
				)}
			</Box>
			<Divider />
			{!isDraft ? <CardContent>
				{materialProducts.length > 0 && <List disablePadding>{materialProducts.map((li) => <ProductListItem 
					key={li.id}
					item={li}
					selectedProductQuantities={selectedProductQuantities}
					onItemClick={onItemClick}
					onQuantityChange={(itemId, newQuantity) => onProductQuantityChange?.(li, newQuantity)}
					disabled={!li.digitalDelivery && !doesShippingRegionMatch}
					maxQuantity={li.digitalDelivery ? 1 : undefined}
					currency={list.currency}
				/>)}</List>}
				{optionalProducts.length > 0 && (
					<>
						<Divider sx={{ my: 1 }} />
						<Typography variant="subtitle2" color="text.secondary">
							{t('planViewQuickOrderCard.optional')}
						</Typography>
						{optionalProducts.map((li) => <ProductListItem 
							key={li.id}
							item={li}
							selectedProductQuantities={selectedProductQuantities}
							onItemClick={onItemClick}
							onQuantityChange={(itemId, newQuantity) => onProductQuantityChange?.(li, newQuantity)}
							disabled={!li.digitalDelivery && !doesShippingRegionMatch}
							maxQuantity={li.digitalDelivery ? 1 : undefined}
							currency={list.currency}
						/>)}
					</>
				)}
				{toolProducts.length > 0 && (
					<>
						<Divider sx={{ my: 1 }} />
						<Typography variant="subtitle2" color="text.secondary">
							{t('planViewQuickOrderCard.tools')}
						</Typography>
						{toolProducts.map((li) => <ProductListItem 
							key={li.id}
							item={li}
							selectedProductQuantities={selectedProductQuantities}
							onItemClick={onItemClick}
							onQuantityChange={(itemId, newQuantity) => onProductQuantityChange?.(li, newQuantity)}
							disabled={!li.digitalDelivery && !doesShippingRegionMatch}
							maxQuantity={li.digitalDelivery ? 1 : undefined}
							currency={list.currency}
						/>)}
					</>
				)}
				{/* tempirarily disabled */}
				{/* {Boolean(missingResources.length || missingTools.length) && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
					{missingResources.length > 0 && (
						<>
							<Alert severity="warning">
								<AlertTitle>
									{t('planViewQuickOrderCard.missingMaterials', { count: missingResources.length })}
								</AlertTitle>
								{missingResources.map(resource => (
									<Typography key={resource.id} variant="body2" color="text.secondary">
										- {resource.name}
									</Typography>
								))}
							</Alert>
						</>
					)}
					{missingTools.length > 0 && (
						<>
							<Alert severity="warning">
								<AlertTitle>
									{t('planViewQuickOrderCard.missingTools', { count: missingTools.length })}
								</AlertTitle>
								{missingTools.map(resource => (
									<Typography key={resource.id} variant="body2" color="text.secondary">
										- {resource.id ? translateToolName(resource.name) : resource.name} {resource.isOptional ? `(${t('planViewQuickOrderCard.optional')})` : ''}
									</Typography>
								))}
							</Alert>
						</>
					)}
				</Box>} */}
				<Box sx={{ 
					mt: 2, 
					pt: 2, 
					borderTop: 1, 
					borderColor: 'divider',
				}}>
					<QuickOrderTotals 
						products={list.products.filter(product => selectedProductQuantities.get(product.id)).map(product => ({
							...product,
							quantity: selectedProductQuantities.get(product.id) as string
						}))}
						additionalFees={list.additionalFees}
						currency={list.currency}
					/>
					{isAddToCartTemporarilyDisabledForVendor ? (
						<Tooltip title="This store is temporarily offline!">
							<span>
								<Button 
									variant="contained" 
									size="large"
									fullWidth
									disabled
									startIcon={selectedQuickOrders.has(list.id) ? <CheckIcon /> : <AddShoppingCartIcon />}
									onClick={handleAddToCart}
									color={selectedQuickOrders.has(list.id) ? 'success' : 'primary'}
								>
									{selectedQuickOrders.has(list.id) ? t('planViewCheckout.inCart') : t('planViewCheckout.addToCart')}
								</Button>
							</span>
						</Tooltip>
					) : (
						<Button 
							variant="contained" 
							size="large"
							fullWidth
							disabled={isCheckoutDisabled}
							startIcon={selectedQuickOrders.has(list.id) ? <CheckIcon /> : <AddShoppingCartIcon />}
							onClick={handleAddToCart}
							color={selectedQuickOrders.has(list.id) ? 'success' : 'primary'}
						>
							{selectedQuickOrders.has(list.id) ? t('planViewCheckout.inCart') : t('planViewCheckout.addToCart')}
						</Button>
					)}
				</Box>
				{/* {list.vendor.vendorContactEmail && <Box sx={{ mt: 2 }}>
					<Typography fontSize='small'>
						<Trans i18nKey="planViewCheckout.requestInfoNotice" components={{ 
							b: <b></b>,
						}} values={{
							vendorEmail: list.vendor.vendorContactEmail
						}} />
					</Typography>
				</Box> } */}
			</CardContent> : null}
			{isDraft && <Alert severity="warning">
				Your shopping list is a work in progress. It will not be visible to customers until it is published.
			</Alert>}
		</Card>
	);
};

interface QuickOrderCardAdjustQuantitySectionProps {
	quantity: number;
	uom: string;
	onQuantityChange: (quantity: number) => void;
	isAddDisabled?: boolean;
	sx?: SxProps<Theme>;
}

export const QuickOrderCardAdjustQuantitySection = ({ quantity, uom, onQuantityChange, isAddDisabled, sx }: QuickOrderCardAdjustQuantitySectionProps) => {
	const theme = useTheme();
	return (
		<Box sx={{ 
			display: 'flex', 
			alignItems: 'center',
			justifyContent: 'center',
			gap: 0.5,
			...sx,
		}}>
			<IconButton 
				onClick={() => {
					const newQuantity = Math.max(0, quantity - 1);
					onQuantityChange(newQuantity);
				}}
				sx={{
					color: 'primary.main',
				}}
			>
				<RemoveIcon />
			</IconButton>
			<Typography sx={{ textAlign: 'center', px: 1, minWidth: '80px', backgroundColor: darken(theme.palette.background.paper, 0.1), borderRadius: 1 }}>
				{quantity} {translateUOM(uom)}
			</Typography>
			<IconButton 
				onClick={() => onQuantityChange(quantity + 1)}
				sx={{
					color: 'primary.main',
				}}
				disabled={isAddDisabled}
			>
				<AddIcon />
			</IconButton>
		</Box>
	);
};